import {
    useState,
    useRef
} from "react";
import {
    useWindowDimensions,
    TouchableOpacity,
    Platform,
    Image,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitSnackBar,
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    TextAreaInput,
    SelectBox,
    Seperator,
    TextInput,
    Dialog,
    Button,
    Switch,
    Text
} from "ncore-ui-kit";
import {
    useNavigation
} from "@react-navigation/native";
import type RootStackParamList from "../../../../navigation/type";
import type {
    EventType
} from "../../type";
import {
    type NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    ArrowLeftIcon,
    ImageIcon,
    SaveIcon
} from "lucide-react-native";
import RESTServiceContext from "../../../../services/restAPI/context";

const EventCreate = () => {
    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const [
        title,
        setTitle
    ] = useState<string>("");

    const [
        description,
        setDescription
    ] = useState<string>("");

    const [
        type,
        setType
    ] = useState<EventType>("standart");

    const [
        isOnlyMembers,
        setIsOnlyMembers
    ] = useState<boolean>(false);

    const [
        moduleName,
        setModuleName
    ] = useState<string>("");

    const [
        logoPreview,
        setLogoPreview
    ] = useState<string>("");

    const [
        coverPreview,
        setCoverPreview
    ] = useState<string>("");

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState<boolean>(false);

    const [
        isDialogVisible,
        setIsDialogVisible
    ] = useState<boolean>(false);

    const logoInputRef = useRef<HTMLInputElement | null>(null);
    const coverInputRef = useRef<HTMLInputElement | null>(null);
    const [
        logoFile,
        setLogoFile
    ] = useState<File | null>(null);
    const [
        coverFile,
        setCoverFile
    ] = useState<File | null>(null);

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const maxFileSize = 5 * 1024 * 1024;
            if (file.size > maxFileSize) {
                NCoreUIKitSnackBar.open({
                    title: localize("members-invalid-file-size") || "Dosya boyutu 5 MB'dan küçük olmalıdır."
                });
                return;
            }
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const handleCoverSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            const maxFileSize = 5 * 1024 * 1024;
            if (file.size > maxFileSize) {
                NCoreUIKitSnackBar.open({
                    title: localize("members-invalid-file-size") || "Dosya boyutu 5 MB'dan küçük olmalıdır."
                });
                return;
            }
            setCoverFile(file);
            setCoverPreview(URL.createObjectURL(file));
        }
    };

    const uploadPhoto = async (
        targetEventID: string,
        file: File,
        tokenEndpoint: string
    ) => {
        try {
            const result = await RESTServiceContext.action(tokenEndpoint as Parameters<typeof RESTServiceContext.action>[0], {
                eventID: targetEventID
            });
            const uploadResult = result as unknown as { payload?: { token?: string; uploadToken?: string } };
            const token = uploadResult.payload?.token || uploadResult.payload?.uploadToken;
            if (token) {
                const formData = new FormData();
                formData.append("file", file);
                await fetch(`${process.env.STORAGE_URL}/upload`, {
                    headers: {
                        Authorization: token
                    },
                    body: formData,
                    method: "POST"
                });
                await new Promise(resolve => setTimeout(resolve, 2000));
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleConfirmSave = async () => {
        setIsDialogVisible(false);
        try {
            setIsSubmitting(true);

            const result = await RESTServiceContext.action("createEvent", {
                isOnlyMembers: isOnlyMembers,
                description: description,
                moduleName: moduleName,
                title: title,
                type: type
            });

            const createResult = result as unknown as { code: number, payload?: { id?: string } };
            if (createResult.code === 200 || createResult.payload) {
                const newEventID = createResult.payload?.id;
                if (newEventID) {
                    if (logoFile) {
                        await uploadPhoto(newEventID, logoFile, "getUploadTokenForEventLogo");
                    }
                    if (coverFile) {
                        await uploadPhoto(newEventID, coverFile, "getUploadTokenForEventCover");
                    }
                }
                NCoreUIKitSnackBar.open({
                    title: localize("events-success-create")
                });
                navigation.navigate("EventsList");
            } else {
                NCoreUIKitSnackBar.open({
                    title: localize("events-error-save")
                });
            }
        } catch (error) {
            console.error(error);
            NCoreUIKitSnackBar.open({
                title: localize("events-error-save")
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSubmit = async () => {
        if (!title.trim()) {
            NCoreUIKitSnackBar.open({
                title: localize("events-title-required")
            });
            return;
        }

        if (!description.trim()) {
            NCoreUIKitSnackBar.open({
                title: localize("events-description-required")
            });
            return;
        }

        setIsDialogVisible(true);
    };

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    alignItems: "center",
                    flexDirection: "row",
                    gap: spaces.spacingMd
                }
            ]}
        >
            <Button
                icon={() => <ArrowLeftIcon
                    color={colors.project.brandWhite}
                    size={24}
                />}
                customBorderColor="secondaryButton"
                onPress={() => navigation.goBack()}
                customColor="secondaryButton"
                spreadBehaviour="free"
                variant="filled"
            />
            <Text
                variant="titleLargeSize"
            >
                {localize("events-create-page-title")}
            </Text>
        </View>;
    };

    const renderImagesSection = () => {
        return <View
            style={[
                stylesheet.sectionContainer,
                {
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.sectionHeader,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Text
                    style={stylesheet.sectionTitle}
                    variant="titleMediumSize"
                >
                    {localize("events-images-section-title")}
                </Text>
                <Text
                    style={[
                        stylesheet.sectionSubtitle,
                        {
                            color: colors.content.text.mid
                        }
                    ]}
                    variant="bodyMediumSize"
                >
                    {localize("events-images-section-subtitle")}
                </Text>
            </View>
            <View
                style={[
                    stylesheet.uploadRow,
                    {
                        flexDirection: isMobile ? "column" : "row"
                    }
                ]}
            >
                {
                    Platform.OS === "web" ?
                        <input
                            onChange={handleLogoSelect}
                            style={{
                                display: "none"
                            }}
                            ref={logoInputRef}
                            accept="image/*"
                            type="file"
                        />
                    :
                        null
                }
                {
                    Platform.OS === "web" ?
                        <input
                            onChange={handleCoverSelect}
                            style={{
                                display: "none"
                            }}
                            ref={coverInputRef}
                            accept="image/*"
                            type="file"
                        />
                    :
                        null
                }
                <TouchableOpacity
                    style={[
                        stylesheet.logoUploadBox,
                        {
                            marginBottom: isMobile ? spaces.spacingLg : 0,
                            backgroundColor: colors.project.brandSurface,
                            marginRight: isMobile ? 0 : spaces.spacingLg,
                            borderColor: colors.project.brandBorder,
                            width: isMobile ? "100%" : 160,
                            borderWidth: borders.seperator,
                            borderRadius: radiuses.md
                        }
                    ]}
                    onPress={() => logoInputRef.current?.click()}
                    activeOpacity={0.8}
                >
                    {
                        logoPreview ?
                            <Image
                                style={stylesheet.uploadedImage}
                                source={{
                                    uri: logoPreview
                                }}
                                resizeMode="cover"
                            />
                        :
                            <View
                                style={[
                                    stylesheet.uploadPlaceholder,
                                    {
                                        gap: spaces.spacingXs
                                    }
                                ]}
                            >
                                <ImageIcon
                                    color={colors.content.icon.mid}
                                    size={32}
                                />
                                <Text
                                    style={stylesheet.uploadLabel}
                                    variant="bodyMediumSize"
                                >
                                    {localize("events-upload-logo")}
                                </Text>
                                <Text
                                    style={[
                                        stylesheet.uploadDimensions,
                                        {
                                            color: colors.content.text.low
                                        }
                                    ]}
                                    variant="labelSmallSize"
                                >
                                    {localize("events-logo-dimensions")}
                                </Text>
                            </View>
                    }
                </TouchableOpacity>
                <TouchableOpacity
                    style={[
                        stylesheet.coverUploadBox,
                        {
                            backgroundColor: colors.project.brandSurface,
                            borderColor: colors.project.brandBorder,
                            width: isMobile ? "100%" : undefined,
                            borderWidth: borders.seperator,
                            borderRadius: radiuses.md
                        }
                    ]}
                    onPress={() => coverInputRef.current?.click()}
                    activeOpacity={0.8}
                >
                    {
                        coverPreview ?
                            <Image
                                style={stylesheet.uploadedImage}
                                source={{
                                    uri: coverPreview
                                }}
                                resizeMode="cover"
                            />
                        :
                            <View
                                style={[
                                    stylesheet.uploadPlaceholder,
                                    {
                                        gap: spaces.spacingXs
                                    }
                                ]}
                            >
                                <ImageIcon
                                    color={colors.content.icon.mid}
                                    size={32}
                                />
                                <Text
                                    style={stylesheet.uploadLabel}
                                    variant="bodyMediumSize"
                                >
                                    {localize("events-upload-cover")}
                                </Text>
                                <Text
                                    style={[
                                        stylesheet.uploadDimensions,
                                        {
                                            color: colors.content.text.low
                                        }
                                    ]}
                                    variant="labelSmallSize"
                                >
                                    {localize("events-cover-dimensions")}
                                </Text>
                            </View>
                    }
                </TouchableOpacity>
            </View>
        </View>;
    };

    const renderBasicInfoSection = () => {
        return <View
            style={[
                stylesheet.sectionContainer,
                {
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.sectionHeader,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Text
                    style={stylesheet.sectionTitle}
                    variant="titleMediumSize"
                >
                    {localize("events-basic-info-title")}
                </Text>
                <Text
                    style={[
                        stylesheet.sectionSubtitle,
                        {
                            color: colors.content.text.mid
                        }
                    ]}
                    variant="bodyMediumSize"
                >
                    {localize("events-basic-info-subtitle")}
                </Text>
            </View>
            <TextInput
                placeholder={localize("events-title-placeholder")}
                onChangeText={(text) => setTitle(text)}
                title={localize("events-title-input")}
                spreadBehaviour="stretch"
                value={title}
            />
            <TextAreaInput
                placeholder={localize("events-description-placeholder")}
                onChangeText={(text) => setDescription(text)}
                title={localize("events-description-input")}
                spreadBehaviour="stretch"
                isUpdateOnRealtime={true}
                value={description}
                numberOfLines={4}
            />
        </View>;
    };

    const renderTypeSection = () => {
        return <View
            style={[
                stylesheet.sectionContainer,
                {
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={[
                    stylesheet.sectionHeader,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Text
                    style={stylesheet.sectionTitle}
                    variant="titleMediumSize"
                >
                    {localize("events-type-section-title")}
                </Text>
                <Text
                    style={[
                        stylesheet.sectionSubtitle,
                        {
                            color: colors.content.text.mid
                        }
                    ]}
                    variant="bodyMediumSize"
                >
                    {localize("events-type-section-subtitle")}
                </Text>
            </View>
            <View
                style={[
                    stylesheet.typeButtonGroup,
                    {
                        borderColor: colors.content.border.subtle,
                        borderWidth: borders.seperator,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingXs,
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Button
                    customBorderColor={type === "standart" ? undefined : "secondaryButton"}
                    customColor={type === "standart" ? undefined : "secondaryButton"}
                    customTextColor={colors.project.brandWhite}
                    title={localize("events-type-standard")}
                    onPress={() => setType("standart")}
                    spreadBehaviour="free"
                    variant="filled"
                />
                <Button
                    customBorderColor={type === "join" ? undefined : "secondaryButton"}
                    customColor={type === "join" ? undefined : "secondaryButton"}
                    customTextColor={colors.project.brandWhite}
                    title={localize("events-type-join")}
                    onPress={() => setType("join")}
                    spreadBehaviour="free"
                    variant="filled"
                />
            </View>
            {
                type === "join" ?
                    <SelectBox
                        titleExtractor={(item: { __title: string; __key: string }) => item.__title}
                        keyExtractor={(item: { __title: string; __key: string }) => item.__key}
                        onChange={(selectedItems) => {
                            const item = selectedItems[0];
                            if (item && typeof item.__key === "string") {
                                setModuleName(item.__key);
                            } else {
                                setModuleName("");
                            }
                        }}
                        data={[
                            {
                                __title: "Modül Yok",
                                __key: ""
                            },
                            {
                                __key: "threeT",
                                __title: "3T"
                            }
                        ]}
                        placeholder="Modül Seçimi"
                        spreadBehaviour="stretch"
                        title="Etkinlik Modülü"
                    />
                :
                    null
            }
        </View>;
    };

    const renderMembersOnlySection = () => {
        return <View
            style={stylesheet.switchRow}
        >
            <View
                style={[
                    stylesheet.switchTextContainer,
                    {
                        gap: spaces.spacingXs
                    }
                ]}
            >
                <Text
                    style={stylesheet.sectionTitle}
                    variant="titleMediumSize"
                >
                    {localize("events-members-only-title")}
                </Text>
                <Text
                    style={[
                        stylesheet.sectionSubtitle,
                        {
                            color: colors.content.text.mid
                        }
                    ]}
                    variant="bodyMediumSize"
                >
                    {localize("events-members-only-subtitle")}
                </Text>
            </View>
            <Switch
                onPress={() => setIsOnlyMembers(!isOnlyMembers)}
                customTheme={{
                    themeKey: "events_form" as never
                }}
                isActive={isOnlyMembers}
                type="neutral"
            />
        </View>;
    };

    const renderFooter = () => {
        return <View
            style={[
                stylesheet.footerActionContainer,
                {
                    backgroundColor: colors.project.brandSurface,
                    borderTopColor: colors.project.brandBorder,
                    paddingHorizontal: spaces.spacingXl,
                    paddingVertical: spaces.spacingLg,
                    borderTopWidth: borders.seperator,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <Button
                onPress={() => navigation.navigate("EventsList")}
                title={localize("events-cancel-button")}
                customBorderColor="secondaryButton"
                customColor="secondaryButton"
                spreadBehaviour="free"
                variant="filled"
                size="small"
            />
            <Button
                icon={({
                    color,
                    size
                }) => <SaveIcon
                    color={colors.project.brandWhite || colors.content.icon[color]}
                    size={size}
                />}
                customTextColor={colors.project.brandWhite}
                title={localize("events-save-button")}
                isLoading={isSubmitting}
                onPress={handleSubmit}
                spreadBehaviour="free"
                variant="filled"
                size="small"
            />
        </View>;
    };

    return <PageContainer
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: spaces.spacingXl,
                    gap: spaces.spacingXl
                }
            ]}
        >
            {renderHeader()}
            <View
                style={[
                    stylesheet.formCard,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderColor: colors.project.brandBorder,
                        borderWidth: borders.seperator,
                        borderRadius: radiuses.lg
                    }
                ]}
            >
                <View
                    style={[
                        stylesheet.formBody,
                        {
                            padding: spaces.spacingXl,
                            gap: spaces.spacingXl
                        }
                    ]}
                >
                    {renderImagesSection()}
                    {renderBasicInfoSection()}
                    {renderTypeSection()}
                    <Seperator
                        color="subtle"
                    />
                    {renderMembersOnlySection()}
                </View>
                {renderFooter()}
            </View>
        </View>
        <Dialog
            primaryButtonProps={{
                title: localize("events-create-confirm-yes"),
                onPress: handleConfirmSave
            }}
            secondaryButtonProps={{
                title: localize("events-create-confirm-no"),
                onPress: () => setIsDialogVisible(false),
                customColor: "secondaryButton",
                variant: "filled"
            }}
            onOverlayPress={() => setIsDialogVisible(false)}
            onClosed={() => setIsDialogVisible(false)}
            isVisible={isDialogVisible}
            variant="yes-no"
        >
            <View
                style={{
                    padding: spaces.spacingLg
                }}
            >
                <Text
                    variant="bodyMediumSize"
                >
                    {localize("events-create-confirm-desc")}
                </Text>
            </View>
        </Dialog>
    </PageContainer>;
};

export default EventCreate;
